import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { Modal } from './common';

/**
 * Promise-based replacement for window.confirm().
 *
 * Native dialogs block the renderer, look wrong on mobile, and can't carry the
 * explanatory text these decisions need — a guard rail change should show the
 * reasoning at the moment it happens, not a one-line OS prompt.
 */

type Request = {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger: boolean;
  resolve: (ok: boolean) => void;
};

type ConfirmFn = (opts: {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);

  const confirm = useCallback<ConfirmFn>(
    ({ title, body, confirmLabel = 'Confirm', danger = false }) =>
      new Promise<boolean>((resolve) => {
        setRequest({ title, body, confirmLabel, danger, resolve });
      }),
    [],
  );

  const settle = useCallback(
    (ok: boolean) => {
      request?.resolve(ok);
      setRequest(null);
    },
    [request],
  );

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {request ? (
        <Modal title={request.title} onClose={() => settle(false)}>
          <div className="muted" style={{ marginTop: 0 }}>
            {request.body}
          </div>
          <div className="row" style={{ marginTop: 16 }}>
            <button
              className={`btn ${request.danger ? 'danger' : 'primary'}`}
              onClick={() => settle(true)}
            >
              {request.confirmLabel}
            </button>
            <button className="btn" onClick={() => settle(false)}>
              Cancel
            </button>
          </div>
        </Modal>
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside ConfirmProvider');
  return ctx;
}
